import { z } from 'zod';

/**
 * A background job the API runs on behalf of a request that would otherwise
 * block.
 *
 * A full inventory sync or a resync opens a chassis session that can run for
 * minutes; held inside the HTTP request it 504s at the proxy on a slow OLT. So
 * the POST enqueues the work, returns a HANDLE immediately, and the client
 * polls this status until the job settles. The chassis commands are unchanged —
 * only where they run moves. See services/jobs/queue.ts.
 */
export const jobStatusValues = ['queued', 'running', 'done', 'failed'] as const;
export type JobRunStatus = (typeof jobStatusValues)[number];

/** What a POST that enqueues a job returns — enough to start polling. */
export const jobHandleSchema = z.object({
  id: z.string(),
  kind: z.string(),
  status: z.enum(jobStatusValues),
  progress: z.number().int().nullable(),
});
export type JobHandle = z.infer<typeof jobHandleSchema>;

/** The full status a poll reads back — carries the result once it is done. */
export const jobStatusSchema = jobHandleSchema.extend({
  message: z.string().nullable(),
  /** The job's own return value, present only once `status` is `done`. */
  result: z.unknown().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  startedAt: z.number().int().nullable(),
  finishedAt: z.number().int().nullable(),
});
export type JobStatus = z.infer<typeof jobStatusSchema>;
